import React, { useState } from "react";
import { WebLink, SiteWide, Images } from "./customComponents";
import Section from "./layouts/Section";
import SecurityCenterModal from "./SecurityCenterModal";
import {
    Typography,
    Card,
    CardContent,
    Grid,
    Box,
    Chip,
    Button,
    Divider,
    Paper
} from '@mui/material';
import {
    AccountBalanceWallet,
    TrendingUp,
    People,
    Security,
    Support,
    Settings,
    Add,
    Remove,
    SwapHoriz,
    History,
    Star,
    Telegram,
    WhatsApp,
    AddTaskOutlined,
    Help,
    AttachMoney,
    ShowChart,
    Group,
    Shield,
    AccessibilityNewTwoTone,
    CreditCard,
    AccountBalance,
    Timeline,
    ToggleOn,
    ToggleOff
} from '@mui/icons-material';
import { money } from "../utility/Helper1";
import { useSelector } from "react-redux";
import { homeScreenStatSelect } from "../redux/selectors";
import { useNavigate } from "react-router-dom";

function Home() {
    const [loadingStat, setLoadingStat] = useState(false);
    const [selectedArrayIndex, setSelectedArrayIndex] = useState(0);
    const [securityModalOpen, setSecurityModalOpen] = useState(false);
    const finance = useSelector(homeScreenStatSelect);
    const navigate  = useNavigate();

    // Portfolio stats with enhanced styling
    const portfolioStats = [
        {
            name: 'Local Wallet',
            value: `${money(finance?.[0]?.balance ?? '0.00', finance?.[0]?.type ?? 22)}`,
            icon: AccountBalanceWallet,
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            glowColor: '103, 126, 234'
        },
        {
            name: 'Local Interest Earned',
            value: `${money(finance?.[0]?.commissionRO ?? '0.00', finance?.[0]?.type ?? 22)}`,
            icon: People,
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            glowColor: '240, 147, 251'
        },
        {
            name: 'Crypto Wallet',
            value: `${money(finance?.[1]?.balance ?? '0.00', finance?.[1]?.type ?? 141)}`,
            icon: SwapHoriz,
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            glowColor: '79, 172, 254'
        },
        {
            name: 'Affiliate Earned',
            value: `${money(finance?.[1]?.commissionRO ?? '0.00', finance?.[1]?.type ?? 141)}`,
            icon: TrendingUp,
            gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            glowColor: '67, 233, 123'
        }
    ];

    // Quick actions with modern icons
    const quickActions = [
        { icon: Add, name: 'Fund Account', gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', to: '/deposit' },
        { icon: Remove, name: 'Withdraw', gradient: 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)', to: '/withdraw' },
        { icon: History, name: 'History', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', to: '/deposit-records' },
    ];

    // Investment services
    const services = [
        { icon: ShowChart, name: 'Bank Vault', desc: 'Automated trading', to: '/bank' },
        { icon: AccountBalanceWallet, name: 'Digital Wallet', desc: 'Secure storage', to: '/wallet' },
        { icon: Group, name: 'Team Management', desc: 'Referral network', to: '/affiliate-center' },
        { icon: Security, name: 'Security Center', desc: 'Account protection', to: null, action: 'modal' },
        { icon: AccessibilityNewTwoTone, name: 'Invite & Earn', desc: 'Referral program', to: '/ref-link' },
        { icon: Settings, name: 'Profile Settings', desc: 'Account management', to: '/personal-information' },
    ];

    const totalPortfolioBalance = ((finance?.[selectedArrayIndex]?.balance || 0)).toLocaleString();

    return (
        <SiteWide title={"Home"}>
            <Section paddingTop={0} hideHeader={true} overlayText={""} loading={false} title={'Home'} style={{}}>
                {/* Hero Balance Section */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        borderRadius: 4,
                        mb: 4,
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at 50% 0%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
                            pointerEvents: 'none',
                        }
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            background: 'linear-gradient(135deg, #a78bfa 0%, #ec4899 50%, #06b6d4 100%)',
                            backgroundClip: 'text',
                            p: 4,
                            pb: 0,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 900,
                            mb: 1,
                            fontSize: { xs: '2.5rem', md: '4rem' }
                        }}
                    >
                        {money(totalPortfolioBalance, finance?.[selectedArrayIndex]?.type ?? 22)}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#94a3b8', mb: 2 }}>
                        Total Portfolio Balance
                    </Typography>

                    {/* Array Toggle */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Button
                            onClick={() => setSelectedArrayIndex(selectedArrayIndex === 0 ? 1 : 0)}
                            sx={{
                                background: 'rgba(139, 92, 246, 0.2)',
                                border: '1px solid rgba(139, 92, 246, 0.4)',
                                borderRadius: 3,
                                px: 3,
                                py: 1.5,
                                color: '#a78bfa',
                                textTransform: 'none',
                                fontWeight: 600,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(139, 92, 246, 0.3)',
                                    border: '1px solid rgba(139, 92, 246, 0.6)',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 8px 24px rgba(139, 92, 246, 0.2)',
                                }
                            }}
                            startIcon={selectedArrayIndex === 0 ? <ToggleOn /> : <ToggleOff />}
                        >

                        </Button>
                    </Box>
                    <div className="text-center" style={{ marginTop: 5, textAlign: 'center', alignItems: 'center', justifyContent: 'center', alignContent: 'center' }}>
                        <Images source={require('../images/barchart2.png')} style={{ maxWidth: '80%', objectFit: 'cover', display: 'block', margin: '0 auto' }} />
                    </div>
                </Paper>

                {/* Portfolio Stats */}
                <Typography
                    variant="h5"
                    sx={{
                        color: '#f8fafc',
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <ShowChart sx={{ color: '#a78bfa' }} />
                    Portfolio Overview
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4, width: '100%' }}>
                    {portfolioStats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <Grid
                                item
                                xs={6}
                                sm={6}
                                md={3}
                                lg={2.4}
                                xl={2}
                                key={index}
                                sx={{
                                    // Ensure full width utilization
                                    minWidth: 0,
                                    flexGrow: 1,
                                }}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'rgba(15, 23, 42, 0.6)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        borderRadius: 3,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        height: '100%', // Ensure equal height
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: `0 20px 40px rgba(${stat.glowColor}, 0.3)`,
                                            border: '1px solid rgba(139, 92, 246, 0.4)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '4px',
                                            background: stat.gradient,
                                        }
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 2,
                                                    background: stat.gradient,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: `0 8px 24px rgba(${stat.glowColor}, 0.3)`,
                                                }}
                                            >
                                                <Icon sx={{ color: 'white', fontSize: '1.8rem' }} />
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1, flexGrow: 1 }}>
                                            {stat.name}
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                                            {stat.value}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1 }} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Quick Actions */}
                <Typography
                    variant="h5"
                    sx={{
                        color: '#f8fafc',
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <AttachMoney sx={{ color: '#a78bfa' }} />
                    Quick Actions
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4, width: '100%' }}>
                    {quickActions.map((action, index) => {
                        const Icon = action.icon;
                        return (
                            <Grid
                                item
                                xs={6}
                                sm={6}
                                md={3}
                                lg={2.4}
                                xl={2}
                                key={index}
                                sx={{
                                    // Ensure full width utilization
                                    minWidth: 0,
                                    flexGrow: 1,
                                }}
                                onClick={() => navigate(action.to)}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'rgba(15, 23, 42, 0.6)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-4px) scale(1.02)',
                                            boxShadow: '0 15px 35px rgba(139, 92, 246, 0.2)',
                                            border: '1px solid rgba(139, 92, 246, 0.4)',
                                        }
                                    }}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: 2,
                                                background: action.gradient,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 16px',
                                                transition: 'transform 0.3s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                }
                                            }}
                                        >
                                            <Icon sx={{ color: 'white', fontSize: '2rem' }} />
                                        </Box>
                                        <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                            {action.name}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Investment Services */}
                <Typography
                    variant="h5"
                    sx={{
                        color: '#f8fafc',
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <CreditCard sx={{ color: '#a78bfa' }} />
                    Investment Services
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {services.map((service, index) => {
                        const Icon = service.icon;
                        return (
                            <Grid
                                onClick={() => service.action === 'modal' ? setSecurityModalOpen(true) : navigate(service.to)}
                                item
                                xs={12}
                                sm={6}
                                md={6}
                                lg={4}
                                xl={3}
                                key={index}
                                sx={{
                                    minWidth: 0,
                                    flexGrow: 1,
                                }}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'rgba(15, 23, 42, 0.6)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 20px 40px rgba(139, 92, 246, 0.2)',
                                            border: '1px solid rgba(139, 92, 246, 0.4)',
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 2,
                                                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mr: 2,
                                                }}
                                            >
                                                <Icon sx={{ color: '#a78bfa', fontSize: '1.5rem' }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                                    {service.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                                    {service.desc}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {1>2 && <>
                    {/* AI Trading Bots Status - Updated to fill horizontal screen */}
                    <Typography
                        variant="h5"
                        sx={{
                            color: '#f8fafc',
                            fontWeight: 700,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Shield sx={{ color: '#a78bfa' }} />
                        AI Trading Bots Status
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 4, width: '100%' }}>
                        {/* Bot Control Panel */}
                        <Grid item xs={12} md={4} lg={3} xl={2.4}>
                            <Card
                                elevation={0}
                                sx={{
                                    background: 'rgba(15, 23, 42, 0.6)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(139, 92, 246, 0.2)',
                                    borderRadius: 3,
                                    height: 280,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 15px 35px rgba(139, 92, 246, 0.2)',
                                        border: '1px solid rgba(139, 92, 246, 0.4)',
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    }
                                }}
                            >
                                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                        <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                            Bot Controls
                                        </Typography>
                                        <Chip
                                            label="5 Active"
                                            size="small"
                                            sx={{
                                                background: 'rgba(34, 197, 94, 0.2)',
                                                color: '#22c55e',
                                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    </Box>

                                    {/* Control Buttons */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
                                        {[
                                            { label: 'Start All', icon: History, color: '#22c55e' },
                                            { label: 'Stop All', icon: History, color: '#ef4444' },
                                            { label: 'Pause All', icon: History, color: '#f59e0b' },
                                        ].map((action, index) => {
                                            const Icon = action.icon;
                                            return (
                                                <Button
                                                    key={index}
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        borderColor: `${action.color}40`,
                                                        color: action.color,
                                                        borderRadius: 2,
                                                        py: 1.2,
                                                        fontSize: '0.9rem',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            borderColor: action.color,
                                                            backgroundColor: `${action.color}10`,
                                                            transform: 'scale(1.02)',
                                                        },
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    }}
                                                    startIcon={<Icon sx={{ fontSize: '1.1rem' }} />}
                                                >
                                                    {action.label}
                                                </Button>
                                            );
                                        })}
                                    </Box>

                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Individual Bot Cards - Fill remaining space */}
                        {[
                            {
                                name: 'Bitcoin Bot',
                                status: 'Active',
                                profit: '+$1,234.56',
                                performance: '94.2%',
                                color: '#22c55e',
                                assets: 'BTC/USDT',
                                trades: '24h: 156',
                                risk: 'Low'
                            },
                            {
                                name: 'Ethereum Bot',
                                status: 'Active',
                                profit: '+$856.23',
                                performance: '87.5%',
                                color: '#22c55e',
                                assets: 'ETH/BTC',
                                trades: '24h: 89',
                                risk: 'Medium'
                            },
                            {
                                name: 'Forex Bot',
                                status: 'Paused',
                                profit: '+$423.18',
                                performance: '78.9%',
                                color: '#f59e0b',
                                assets: 'EUR/USD',
                                trades: '24h: 34',
                                risk: 'High'
                            },
                        ].map((bot, index) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={6}
                                lg={4}
                                xl={3.33}
                                key={index}
                                sx={{
                                    minWidth: 0,
                                    flexGrow: 1,
                                }}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'rgba(15, 23, 42, 0.6)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        borderRadius: 3,
                                        height: 280,
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 15px 35px rgba(${bot.color === '#22c55e' ? '34, 197, 94' :
                                                bot.color === '#f59e0b' ? '245, 158, 11' : '167, 139, 250'}, 0.2)`,
                                            border: '1px solid rgba(139, 92, 246, 0.4)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '4px',
                                            background: bot.color === '#22c55e' ?
                                                'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' :
                                                bot.color === '#f59e0b' ?
                                                    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                                                    'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)',
                                        }
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box>
                                                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600, mb: 0.5 }}>
                                                    {bot.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                                    {bot.assets}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={bot.status}
                                                size="small"
                                                sx={{
                                                    backgroundColor: `${bot.color}20`,
                                                    color: bot.color,
                                                    border: `1px solid ${bot.color}40`,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 2, flexGrow: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 0.5, fontSize: '0.8rem' }}>
                                                24h Profit
                                            </Typography>
                                            <Typography variant="h5" sx={{ color: bot.color, fontWeight: 700, mb: 1 }}>
                                                {bot.profit}
                                            </Typography>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                                    Performance
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {bot.performance}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                                    {bot.trades}
                                                </Typography>
                                                <Chip
                                                    label={bot.risk}
                                                    size="small"
                                                    sx={{
                                                        background: bot.risk === 'Low' ? 'rgba(34, 197, 94, 0.2)' :
                                                            bot.risk === 'Medium' ? 'rgba(245, 158, 11, 0.2)' :
                                                                'rgba(239, 68, 68, 0.2)',
                                                        color: bot.risk === 'Low' ? '#22c55e' :
                                                            bot.risk === 'Medium' ? '#f59e0b' : '#ef4444',
                                                        border: `1px solid ${bot.risk === 'Low' ? 'rgba(34, 197, 94, 0.3)' :
                                                            bot.risk === 'Medium' ? 'rgba(245, 158, 11, 0.3)' :
                                                                'rgba(239, 68, 68, 0.3)'}`,
                                                        fontSize: '0.65rem',
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>}

            </Section>

            {/* Security Center Modal */}
            <SecurityCenterModal 
                open={securityModalOpen} 
                onClose={() => setSecurityModalOpen(false)} 
            />
        </SiteWide>
    );
}

export default Home;